import { Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, ViewEncapsulation } from "@angular/core";
import { NgbTabChangeEvent } from "@ng-bootstrap/ng-bootstrap";
import { LaboratoriesService } from "../../../services/laboratories.service";
import { LaboratoryRequestIndicatorsService } from "../../../services/laboratory-request-indicators.service";
import { forkJoin } from "rxjs";
import { LaboratoryIndicatorDto } from "../../../models/laboratory/laboratory-indicator.dto";
import { LaboratoryRequestIndicatorListItem } from "../../../models/laboratory/laboratory-request-indicator-list-item";
import { CellClickEvent, GridComponent } from "@progress/kendo-angular-grid";
import { KeyValueObject } from "../../../models/core/KeyValueObject";
import { UsersService } from "../../../../admin/users/users.service";
import { LaboratoryExaminationMethodListItem } from "../../../models/laboratory/laboratory-examination-method-list-item";
import { DropDownFilterSettings } from "@progress/kendo-angular-dropdowns";
import { LaboratoryExaminationEquipmentDto } from "../../../models/laboratory/laboratory-examination-equipment.dto";
import { LaboratoryRequestExaminationEquipmentDto } from "../../../models/laboratory/laboratory-request-examination-equipment.dto";
import { LaboratoryRequestExaminationEquipmentsService } from "../../../services/laboratory-request-examination-equipments-service";
import { UserGroupsEnum } from "../../../enums/user-groups-enum";
import { RequestStatus } from "../../../enums";
import { NotificationService } from "../../../../core/services/notification.service";
import { AppService } from "../../../../app.service";
import { DataSourceRequestState } from "@progress/kendo-data-query";
import { RequestStatusKindEnum } from "../../../enums/request-status-kind.enum";
import { LookupService } from "../../../services/lookup.service";
import { SecurityService } from "../../../../core/services/security.service";
import { NotificationType } from "../../../../core/services/notification-type";

@Component({
    selector: 'request-page-water-sampling',
    templateUrl: './request-page-water-sampling.component.html',
    styleUrls: ['./request-page-water-sampling.component.scss',
        '../../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ],
    encapsulation: ViewEncapsulation.None,
})
export class RequestPageWaterSamplingComponent implements OnInit {
    
    @ViewChild("requestIndicatorsGrid") requestIndicatorsGrid: GridComponent;

    @Input() requestId: number;
    @Input() requestStatusId: number;

    @Output() correctStatus: EventEmitter<number> = new EventEmitter<number>();

    dropDownFilterSettings: DropDownFilterSettings = { caseSensitive: false, operator: 'contains' };
    activeIdString: string = "requestIndicators";

    indicatorsLoading: boolean = false;
    equipmentsLoading: boolean = false;

    requestIndicators: LaboratoryRequestIndicatorListItem[] = [];
    requestExaminationEquipments: LaboratoryRequestExaminationEquipmentDto[] = [];
    
    requestStatusesFinal: KeyValueObject[] = [];
    performerUsers: KeyValueObject[] = [];
    laboratoryIndicatorsList: LaboratoryIndicatorDto[] = [];
    laboratoryIndicators: KeyValueObject[] = [];
    laboratoryExaminationMethodsList: LaboratoryExaminationMethodListItem[] = [];
    laboratoryExaminationMethods: KeyValueObject[] = [];
    laboratoryExaminationEquipments: LaboratoryExaminationEquipmentDto[] = [];
    editedRequestIndicatorGridRowIndex: number;
    editedRequestIndicator: LaboratoryRequestIndicatorListItem = null;

    constructor(
        private laboratoriesService: LaboratoriesService,
        private laboratoryRequestIndicatorsService: LaboratoryRequestIndicatorsService,
        private laboratoryRequestExaminationEquipmentsService: LaboratoryRequestExaminationEquipmentsService,
        private usersService: UsersService,
        private notificationService: NotificationService,
        private appService: AppService,
        private lookupService: LookupService,
        private securityService: SecurityService
    ) { }

    ngOnInit(): void {
        this.indicatorsLoading = true;
        this.equipmentsLoading = true;

        var requestStatusesStateFinal: DataSourceRequestState = {
            skip: 0,
            take: null,
            filter: { logic: "and", filters: [{ field: "StatusKindId", operator: "eq", value: RequestStatusKindEnum.final }] }
        };

        const laboratoryIndicators$ = this.laboratoriesService.getAllIndicators();
        const laboratoryExaminationMethods$ = this.laboratoriesService.getAllExaminationMethods();
        const laboratoryExaminationEquipments$ = this.laboratoriesService.getAllExaminationEquipments();
        const performerUsers$ = this.usersService.getUsersByGroup(UserGroupsEnum.laboratoryEmployees); // TODO
        const requestIndicators$ = this.laboratoryRequestIndicatorsService.getAllByRequest(this.requestId);
        const requestExaminationEquipments$ = this.laboratoryRequestExaminationEquipmentsService.getAllByRequest(this.requestId);
        const requestStatusesFinal$ = this.lookupService.getData("request-statuses", requestStatusesStateFinal);

        forkJoin(
            laboratoryIndicators$, 
            laboratoryExaminationMethods$, 
            laboratoryExaminationEquipments$,
            performerUsers$, 
            requestIndicators$,
            requestExaminationEquipments$,
            requestStatusesFinal$)
        .subscribe(([
            laboratoryIndicators, 
            laboratoryExaminationMethods, 
            laboratoryExaminationEquipments,
            performerUsers, 
            requestIndicators,
            requestExaminationEquipments,
            requestStatusesFinal]) => 
        {
            this.laboratoryIndicatorsList = laboratoryIndicators.data;
            this.laboratoryIndicators = laboratoryIndicators.data.map(x => new KeyValueObject(x.laboratoryIndicatorId, x.name));
            this.laboratoryExaminationMethodsList = laboratoryExaminationMethods.data;
            this.laboratoryExaminationMethods = laboratoryExaminationMethods.data.map(x => new KeyValueObject(x.laboratoryExaminationMethodId, x.name));
            this.laboratoryExaminationEquipments = laboratoryExaminationEquipments.data;
            this.performerUsers = performerUsers;
            this.requestExaminationEquipments = requestExaminationEquipments;

            this.requestIndicators = requestIndicators;
            this.requestIndicators.filter(x => x.shouldBeDoneDateTime != null).forEach(x => {
                x.shouldBeDoneDateTime = this.appService.parseDate(x.shouldBeDoneDateTime);
            });
            this.requestIndicators.filter(x => x.wasDoneDateTime != null).forEach(x => {
                x.wasDoneDateTime = this.appService.parseDate(x.wasDoneDateTime);
            });

            this.laboratoryExaminationEquipments.forEach(x => {
                x.usedInRequest = this.requestExaminationEquipments.some(r => 
                    r.laboratoryExaminationEquipmentId == x.laboratoryExaminationEquipmentId)
            });

            this.requestStatusesFinal = requestStatusesFinal;

            this.indicatorsLoading = false;
            this.equipmentsLoading = false;
        });
    }

    loadEquipments() {
        const laboratoryExaminationEquipments$ = this.laboratoriesService.getAllExaminationEquipments();
        const requestExaminationEquipments$ = this.laboratoryRequestExaminationEquipmentsService.getAllByRequest(this.requestId);

        forkJoin(laboratoryExaminationEquipments$, requestExaminationEquipments$)
        .subscribe(([laboratoryExaminationEquipments, requestExaminationEquipments]) => 
        {
            this.laboratoryExaminationEquipments = laboratoryExaminationEquipments.data;
            this.requestExaminationEquipments = requestExaminationEquipments;

            this.laboratoryExaminationEquipments.forEach(x => {
                x.usedInRequest = this.requestExaminationEquipments.some(r => 
                    r.laboratoryExaminationEquipmentId == x.laboratoryExaminationEquipmentId)
            });

            this.equipmentsLoading = false;
        });
    }

    tabChanged(e: NgbTabChangeEvent) {
        this.activeIdString = e.nextId;
    }

    createNewRequestIndicator(): LaboratoryRequestIndicatorListItem {
        var indicator = new LaboratoryRequestIndicatorListItem();
        indicator.requestId = this.requestId;
        return indicator;
    }
    
    addNewRequestIndicator() {
        this.requestIndicatorsGrid.addRow(this.createNewRequestIndicator());
    }

    laboratoryIndicatorChange(item: LaboratoryRequestIndicatorListItem) {
        if (item.laboratoryIndicatorId && !item.laboratoryExaminationMethodId) {
            let examinationMethod = this.laboratoryExaminationMethodsList.find(x => 
                x.defaultLaboratoryIndicatorId == item.laboratoryIndicatorId);

            item.laboratoryExaminationMethodId = examinationMethod.laboratoryExaminationMethodId;
            this.examinationMethodChange(item);
        }
    }
    
    examinationMethodChange(item: LaboratoryRequestIndicatorListItem) {
        let selectedExaminationMethod = this.laboratoryExaminationMethodsList.find(x => 
            x.laboratoryExaminationMethodId == item.laboratoryExaminationMethodId);

        if (selectedExaminationMethod != null) {
            item.performerUserId = selectedExaminationMethod.assignedPerformerUserId;
            if (!item.laboratoryIndicatorId) {
                item.laboratoryIndicatorId = selectedExaminationMethod.defaultLaboratoryIndicatorId;
            }

            const today = new Date();
            const nextWeek = new Date();
            nextWeek.setDate(today.getDate() + 7)
            item.shouldBeDoneDateTime = nextWeek;
        }
    }

    saveRequestIndicator({ dataItem, rowIndex }: any ) {
        if (!this.hasInaccurary(dataItem)) {
            dataItem.inaccuracyText = null;
        }

        dataItem.valueText = this.formatStringNumber(dataItem.valueText);
        dataItem.inaccuracyText = this.formatStringNumber(dataItem.inaccuracyText);
        
        let valueText: string = dataItem.valueText;
        let inaccuracyText: string = dataItem.inaccuracyText;

        if (this.isEmpty(valueText) && this.isEmpty(inaccuracyText)) {
            this.saveRequestIndicatorImpl(dataItem);
        }
        else if (!valueText.includes('<') && !valueText.includes('>')) {
            if (this.isNotNumber(valueText) && this.isNotNumber(valueText.slice(1, valueText.length))) {
                this.showIndicatorsError("Введите корректное значение показателя", rowIndex);
            }
            else if (this.hasInaccurary(dataItem) && this.isEmpty(inaccuracyText)) {
                this.showIndicatorsError("Не указана погрешность", rowIndex);
            }
            else if (this.hasInaccurary(dataItem) && this.isNotNumber(inaccuracyText)) {
                this.showIndicatorsError("Введите корректное значение погрешности", rowIndex);
            }
            else {
                dataItem.value = this.buildNumber(valueText);
                dataItem.inaccuracy = this.buildNumber(inaccuracyText);
                dataItem.exceeding = 0;

                this.saveRequestIndicatorImpl(dataItem);
            }
        }
        else if (this.isNotNumber(valueText) && this.isNotNumber(valueText.slice(1, valueText.length))) {
            this.showIndicatorsError("Введите корректное значение показателя", rowIndex);
        }
        else {
            dataItem.value = this.buildNumber(valueText.slice(1, valueText.length));
            dataItem.inaccuracy = null;
            dataItem.exceeding = valueText.includes('>') ? 1 : -1;

            this.saveRequestIndicatorImpl(dataItem);
        }
    }

    showIndicatorsError(text: string, rowIndex: number) {
        this.notificationService.error({
            title: text,
            message: "",
            notificationType: NotificationType.Toast,
        });
        setTimeout(() => this.requestIndicatorsGrid.editRow(rowIndex), 10);
    }

    saveRequestIndicatorImpl(dataItem: LaboratoryRequestIndicatorListItem) {
        this.indicatorsLoading = true;
        if (dataItem.laboratoryRequestIndicatorId == null || dataItem.laboratoryRequestIndicatorId == 0) {
            this.laboratoryRequestIndicatorsService.create(dataItem).subscribe(() => {
                this.editedRequestIndicator = null;
                this.loadRequestIndicators();
            });
        }
        else {
            this.laboratoryRequestIndicatorsService.update(dataItem, null, true, true).subscribe(() => {
                this.editedRequestIndicator = null;
                this.loadRequestIndicators();
            });
        }
    }

	cancelRequestIndicator({ dataItem, sender }) {
		this.closeCurrentRequestIndicatorGridRow(sender);
		this.editedRequestIndicatorGridRowIndex = null;
		this.editedRequestIndicator = null;
	}

    requestIndicatorsGridCellClick(event: CellClickEvent): void {
        if (!this.canEditRequestWaterSampling) {
            return;
        }

        this.requestIndicatorsGridEdit(event);
    }

    requestIndicatorsGridEdit({ sender, rowIndex, dataItem }) {
		this.closeCurrentRequestIndicatorGridRow(sender);
		this.editedRequestIndicatorGridRowIndex = rowIndex;
		this.editedRequestIndicator = {...dataItem};
        
        if (dataItem.wasDoneDateTime == null && this.canEditRequestWaterSamplingValue) {
            dataItem.wasDoneDateTime = this.appService.parseDate(new Date());
        }

		sender.editRow(rowIndex);
	}

    private closeCurrentRequestIndicatorGridRow(grid: GridComponent): void {
		if (this.editedRequestIndicatorGridRowIndex != null && this.editedRequestIndicatorGridRowIndex + 1) {
			grid.closeRow(this.editedRequestIndicatorGridRowIndex);

			if (this.editedRequestIndicator != null) {
				let currentIndicator = this.requestIndicators.find(x => 
					x.laboratoryRequestIndicatorId == this.editedRequestIndicator.laboratoryRequestIndicatorId);	

                currentIndicator.laboratoryIndicatorId = this.editedRequestIndicator.laboratoryIndicatorId;
                currentIndicator.value = this.editedRequestIndicator.value;
                currentIndicator.valueText = this.editedRequestIndicator.valueText;
                currentIndicator.inaccuracyText = this.editedRequestIndicator.inaccuracyText;
                currentIndicator.comment = this.editedRequestIndicator.comment;
                currentIndicator.performerUserId = this.editedRequestIndicator.performerUserId;
                currentIndicator.shouldBeDoneDateTime = this.editedRequestIndicator.shouldBeDoneDateTime;
                currentIndicator.wasDoneDateTime = this.editedRequestIndicator.wasDoneDateTime;
				this.editedRequestIndicator = null;
			}
		}
    }

    @HostListener('window:keydown.enter')
    onEnterKeyDown(): void {
        if (this.editedRequestIndicatorGridRowIndex + 1) {
			let dataItem = this.requestIndicators.find(x => x.laboratoryRequestIndicatorId == this.editedRequestIndicator.laboratoryRequestIndicatorId);
            let rowIndex = this.editedRequestIndicatorGridRowIndex;
            this.requestIndicatorsGrid.closeRow(rowIndex);
            setTimeout(() => this.saveRequestIndicator({ dataItem, rowIndex }), 10);
        }
    }

    removeRequestIndicator({ dataItem }: any) {
        this.indicatorsLoading = true;
        this.laboratoryRequestIndicatorsService.remove(dataItem.laboratoryRequestIndicatorId.toString(), null, null, true).subscribe(() => {
            this.loadRequestIndicators();
        });
    }

    turnExaminationEquipment(event: any, item: LaboratoryExaminationEquipmentDto) {
        this.equipmentsLoading = true;

        if (event.target.checked) {
            let newEquipment = {
                requestId: this.requestId,
                laboratoryExaminationEquipmentId: item.laboratoryExaminationEquipmentId
            } as LaboratoryRequestExaminationEquipmentDto;

            this.laboratoryRequestExaminationEquipmentsService.create(newEquipment, null, true).subscribe(() => {
                this.loadEquipments();
            });
        }
        else {
            let equipmentId = this.requestExaminationEquipments.find(x => 
                x.laboratoryExaminationEquipmentId == item.laboratoryExaminationEquipmentId).laboratoryRequestExaminationEquipmentId;
            
            this.laboratoryRequestExaminationEquipmentsService.remove(equipmentId.toString(), null, null, true).subscribe(() => {
                this.loadEquipments();
            });
        }
    }

    isValidIndicatorValue(item: LaboratoryRequestIndicatorListItem): boolean {
        return item.value == null ||
            ((item.normalValueMin == null || item.value >= item.normalValueMin) &&
            (item.normalValueMax == null || item.value <= item.normalValueMax));
    }

    formatStringNumber(value: string): string {
        if (value != null && value.includes('.')) {
            value = value.replace('.', ',');
        }
        return value;
    } 

    onKeyPress(event: KeyboardEvent, allowSpecChars: boolean = true) {
        const input = event.target as HTMLInputElement;
        const value = input.value;
        const key = event.key;
        const cursorPosition = input.selectionStart || 0;
        const dotChars = ['.', ','];

        // Разрешаем: цифры, Backspace, Delete, стрелки и т.д.
        if (['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(key)) {
            return true;
        }

        if (!allowSpecChars && ['<', '>', '-'].includes(key)) {
            event.preventDefault();
            return false;
        }

        // Разрешаем в начале символы '<>,'
        if ((['<', '>', '-'].includes(key) && cursorPosition === 0) || 
            (cursorPosition == 1 && ['<', '>'].includes(value[0]) && key === '-')) 
        {
            return true;
        }

        // Запрещаем запятную после символов '<>,'
        if (cursorPosition !== 0 && dotChars.includes(key) && !/[0-9]/.test(value[cursorPosition - 1])) {
            event.preventDefault();
            return false;
        }

        // Разрешаем точку, только если её ещё нет и она не в начале/конце
        if (dotChars.includes(key)) {
            const dotIndex = value.indexOf(',');
            const isAtStart = cursorPosition === 0;
            
            if (dotIndex === -1 && !isAtStart) {
                // заменяем точку на запятую
                if (key === '.') {
                    input.value = value.slice(0, cursorPosition) + ',' + value.slice(cursorPosition);
                    event.preventDefault();
                    return false;
                }

                return true;
            } else {
                event.preventDefault();
                return false;
            }
        }

        // Разрешаем только цифры
        if (!/[0-9]/.test(key)) {
            event.preventDefault();
            return false;
        }

        return true;
    }

    validAccuracy(item: LaboratoryRequestIndicatorListItem): boolean {
		let validAccuracy = false;

		if (!this.canEditRequestWaterSamplingValue || item.accuracy == null || item.accuracy == 0) {
			validAccuracy = true;
		}
		else if (item.valueText) {
			var itemParts = item.valueText.split(',');
			if (itemParts.length == 2) {
				validAccuracy = itemParts[1].trim().length >= item.accuracy;
			}
		}

		return validAccuracy;
	}

	getDoubleAccuracy(item: LaboratoryRequestIndicatorListItem) {
		return item.accuracy != null
			? '0.' + '0'.repeat(item.accuracy - 1) + '1'
			: '0';
	}

    getLaboratoryIndicatorName(item: LaboratoryRequestIndicatorListItem) {
        let indicatorName = item.laboratoryIndicatorName ? item.laboratoryIndicatorName : '';
        let indicatorUnit = item.laboratoryIndicatorUnit ? item.laboratoryIndicatorUnit : '';

        if (indicatorUnit.trim() != '') {
            indicatorName += `, ${indicatorUnit}`;
        }

        return indicatorName;
    }

    isNewRequestIndicator(item: LaboratoryRequestIndicatorListItem): boolean {
        return item == null || item.laboratoryRequestIndicatorId == null || item.laboratoryRequestIndicatorId == 0;
    }

    correctRequestWaterSampling() {
        this.correctStatus.emit(<number>RequestStatus.TrialProtocolAdjustment);
    }

    private loadRequestIndicators() {
        this.laboratoryRequestIndicatorsService.getAllByRequest(this.requestId).subscribe(data => {
            this.requestIndicators = data;
            this.requestIndicators.filter(x => x.shouldBeDoneDateTime != null).forEach(x => {
                x.shouldBeDoneDateTime = this.appService.parseDate(x.shouldBeDoneDateTime);
            });
            this.requestIndicators.filter(x => x.wasDoneDateTime != null).forEach(x => {
                x.wasDoneDateTime = this.appService.parseDate(x.wasDoneDateTime);
            });
            this.indicatorsLoading = false;
        });
    }

    public rowClass() {
        return {
            'cursor-pointer': true
        };
    }

    isNotNumber(value: string): boolean {
        return isNaN(this.buildNumber(value));
    }

    buildNumber(value: string): number {
        if (!this.isEmpty(value)) {
            value = value.replace(',', '.');
            return Number(value);
        }

        return NaN;
    }

    isEmpty(value: string) {
		return value == null || value.trim() == '';
	}

    hasInaccurary(item: LaboratoryRequestIndicatorListItem): boolean {
        if (item.laboratoryIndicatorId) {
            let indicatorItem = this.laboratoryIndicatorsList.find(x => x.laboratoryIndicatorId == item.laboratoryIndicatorId);
            return indicatorItem.code != 'smell';
        }

        return true;
    }

    get contentHeight(): number {
        return window.innerHeight - 250;
    }

    get canEditRequestWaterSampling(): boolean {
        return this.requestStatusId == <number>RequestStatus.TrialExecution ||
            this.requestStatusId == <number>RequestStatus.TrialTaskPreparation ||
            this.requestStatusId == <number>RequestStatus.TrialsCompleted ||
            this.requestStatusId == <number>RequestStatus.TrialProtocolAdjustment
    }
    
    get canEditRequestWaterSamplingValue(): boolean {
        return this.requestStatusId == <number>RequestStatus.TrialExecution || 
            this.requestStatusId == <number>RequestStatus.TrialsCompleted ||
            this.requestStatusId == <number>RequestStatus.TrialProtocolAdjustment
    }

    get canCorrectRequestWaterSampling(): boolean {
        return this.requestStatusesFinal.some(x => x.id == this.requestStatusId) && 
            this.securityService.hasClaim('EditRequestPerformerInSelectedSpecStates');
    }

    get userIsEngineerRight(): boolean {
        return this.securityService.hasClaim('role-Engineer-right');
    }
}